import { FirmwareUpdate } from "./dtos/FirmwareUpdate.schema.d";
import { DevicePropertyDescription } from "./dtos/DevicePropertyDescription.schema.d";
import { Pricing } from "./dtos/Pricing.schema.d";
import { RentalMethod } from "./dtos/RentalMethod.schema";
import { AvailableDevices } from "./dtos/AvailableDevices.schema.d";
import { Station, StationLocation } from "./dtos/Station.schema.d";
import axios from "axios";
import { StationSummary } from "./dtos/StationSummary.schema";
import { DeviceTypeDescription } from "./dtos/DeviceTypeDescription.schema.d";
import { DeviceDimensions } from "./dtos/DeviceDimensions.schema";
import { StationSupportContacts } from "./dtos/StationSupportContacts.schema";
import { DeviceTypeSettings } from "./dtos/DeviceTypeSettings.schema";
import { Device } from "./dtos/Device.schema";
import { DeviceAccessUser } from "@/api/dtos/DeviceAccessUser.schema";
import { DeviceAccessAddedResult } from "@/api/dtos/DeviceAccessAddedResult.schema";
import { DeviceUtilizationDto } from "@/api/dtos/DeviceUtilizationDto";
import { CurrentDeviceStatusDto } from "./dtos/CurrentDeviceStatusDto";
import { DeviceHardwareSettings } from "./dtos/DeviceHardwareSettings.schema";
import { BookingCardOccupationsResponseDto } from "./dtos/BookingCardOccupations.schema.d";
import { BookingDeviceFilterOption } from "./dtos/BookingDeviceFilterOption";

export interface ExclusiveRentalPublicSettings {
  visible: boolean;
  text: string | null;
}

export const getStations = async (): Promise<StationSummary[]> => {
  const res = await axios.get<StationSummary[]>("/stations");
  return res.data;
};

export const getStationsByOperator = async (operatorId: string): Promise<StationSummary[]> => {
  const res = await axios.get<StationSummary[]>(`/stations/operator/${operatorId}`);
  return res.data;
};

export const getBookingCardOccupations = async (
  stationId: string,
  start: Date | string,
  end: Date | string
): Promise<BookingCardOccupationsResponseDto> => {
  const startParam = encodeURIComponent(toApiInstantDateTime(start));
  const endParam = encodeURIComponent(toApiInstantDateTime(end));
  const res = await axios.get<BookingCardOccupationsResponseDto>(
    `/stations/${stationId}/booking-cards/occupations?start=${startParam}&end=${endParam}`
  );

  return res.data;
};

export const createStation = async (station: Station): Promise<Station> => {
  const res = await axios.post<Station>("/stations", station);
  return res.data;
};

export const getStation = async (stationId: string): Promise<Station> => {
  const res = await axios.get<Station>(`/stations/${stationId}`);
  return res.data;
};

export const getBookingFilterDevices = async (
  stationId: string
): Promise<BookingDeviceFilterOption[]> => {
  const res = await axios.get<BookingDeviceFilterOption[]>(
    `/stations/${stationId}/devices`
  );
  return res.data;
};

export const getDeviceTypes = async (): Promise<string[]> => {
  const res = await axios.get<string[]>("/stations/device-types");
  return res.data;
};

export const getDeviceProperties = async (): Promise<string[]> => {
  const res = await axios.get<string[]>("/stations/device-properties");
  return res.data;
};

export const createDeviceType = async (type: string): Promise<void> => {
  const res = await axios.post("/stations/device-types", { name: type });
  console.log(res.status);
};

export const createDeviceProperty = async (property: string): Promise<void> => {
  const res = await axios.post("/stations/device-properties", {
    name: property
  });
  console.log(res.status);
};

export const deleteDeviceType = async (type: string): Promise<void> => {
  const res = await axios.delete(
    `/stations/device-types/${encodeURIComponent(type)}`
  );
  console.log(res.status);
};

export const deleteDeviceProperty = async (property: string): Promise<void> => {
  const res = await axios.delete(
    `/stations/device-properties/${encodeURIComponent(property)}`
  );

  console.log(res.status);
};

export const deleteStation = async (stationId: string): Promise<void> => {
  const res = await axios.delete<void>(`/stations/${stationId}`);
  console.log(res.status);
};

export const getBoxPrice = async (
  stationId: string,
  start: Date | string,
  end: Date | string,
  device: Device
): Promise<string> => {
  const res = await axios.post<string>(
    `/stations/device/price`,
    {
      stationId: stationId,
      start: toApiDateTime(start),
      end: toApiDateTime(end),
      device: device
    }
  );
  return res.data;
};

export const getAvailableDevices = async (
  stationId: string,
  start: Date | string,
  end: Date | string,
  rentalMethod: RentalMethod | undefined = undefined,
  deviceType: string | undefined = undefined,
  deviceProperties: string[] | undefined = undefined
): Promise<AvailableDevices> => {
  const res = await axios.post<AvailableDevices>(
    `/stations/${stationId}/available`,
    {
      start: toApiDateTime(start),
      end: toApiDateTime(end),
      deviceType,
      rentalMethod,
      deviceProperties
    }
  );
  return res.data;
};

export const filterAvailableDevices = async (
  stationIds: string[],
  start: Date | string,
  end: Date | string,
  rentalMethod: RentalMethod | undefined = undefined,
  deviceTypes: string[] | undefined = undefined,
  deviceProperties: string[] | undefined = undefined
): Promise<AvailableDevices> => {
  const res = await axios.post<AvailableDevices>(
    `/stations/devices/available`,
    {
      stationIds: stationIds,
      start: toApiDateTime(start),
      end: toApiDateTime(end),
      deviceTypes: deviceTypes,
      rentalMethod,
      deviceProperties
    }
  );
  console.log(res);
  return res.data;
};

export const getUtilization = async (
  stationId: string,
  start: Date,
  end: Date,
  deviceType: string | null
): Promise<DeviceUtilizationDto[]> => {
  const res = await axios.get<DeviceUtilizationDto[]>(
    `/stations/${stationId}/utilization`,
    {
      params: {
        start: start.toISOString().split("T")[0],
        end: end.toISOString().split("T")[0],
        deviceType: deviceType
      }
    }
  );

  return res.data;
};

export const getCurrentUtilization = async (
  stationId: string
): Promise<CurrentDeviceStatusDto[]> => {
  const res = await axios.get<CurrentDeviceStatusDto[]>(
    `/stations/${stationId}/current-utilization`
  );

  return res.data;
};

export const getControllerToken = async (
  stationId: string
): Promise<string> => {
  const res = await axios.get<string>(
    `/stations/${stationId}/token`
  );

  return res.data;
};

export const reboot = async (
  stationId: string
): Promise<boolean> => {
  const res = await axios.post(
    `/stations/${stationId}/reboot`
  );
  return res.status >= 200 && res.status < 300;
};

export const update = async (
  stationId: string,
  firmwareVersionId: number
): Promise<boolean> => {
  const res = await axios.post(
    `/stations/${stationId}/update`, firmwareVersionId,
    {
      headers: {
        "Content-Type": "application/json"
      }
    }
  );
  return res.status >= 200 && res.status < 300;
};

export const getDeviceAccessUsers = async (
  stationId: string,
  deviceNumber: number
): Promise<DeviceAccessUser[]> => {
  if (stationId == null || stationId == "") return [];
  const res = await axios.get<DeviceAccessUser[]>(
    `/stations/${stationId}/devices/${deviceNumber}/accesses`
  );

  return res.data;
};

export const addDeviceAccessUser = async (
  stationId: string,
  deviceNumber: number,
  email: string
): Promise<DeviceAccessAddedResult> => {
  const res = await axios.put<DeviceAccessAddedResult>(
    `/stations/${stationId}/devices/${deviceNumber}/accesses`,
    { email: email }
  );
  return res.data;
};

export const removeDeviceAccessUser = async (
  stationId: string,
  deviceNumber: number,
  id: string
): Promise<void> => {
  const res = await axios.delete<void>(
    `/stations/${stationId}/devices/${deviceNumber}/accesses/${id}`
  );
  return res.data;
};

export const updateStationImage = async (
  stationId: string,
  imageFile: File
): Promise<void> => {
  const res = await axios.put<void>(
    `/stations/${stationId}/location/image`,
    imageFile,
    {
      headers: {
        "Content-Type": imageFile.type
      }
    }
  );
  console.log(res.status);
};

export const getStationImage = async (
  stationId: string
): Promise<File | undefined> => {
  const res = await axios.get(`/stations/${stationId}/location/image`, {
    responseType: "blob"
  });
  // const res = await axios.get(
  //   'http://localhost:8080/station_placeholder.png',
  //   {
  //     responseType: 'blob'
  //   }
  // )
  return res?.data;
};

export const updateIsStationActive = async (
  stationId: string,
  isStationActive: boolean
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/is-active`,
    isStationActive,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updatePlatformServiceActive = async (
  stationId: string,
  platformServiceActive: boolean
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/location/platform-service`,
    platformServiceActive,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateStationLocation = async (
  stationId: string,
  location: StationLocation
): Promise<void> => {
  const res = await axios.put(`/stations/${stationId}/location`, location);
  console.log(res.status);
};

export const updateStationOperator = async (
  stationId: string,
  operatorId: string
): Promise<void> => {
  const res = await axios.put(`/stations/${stationId}/operator`, {
    uuid: operatorId
  });
  console.log(res.status);
};

export const updateStationPricing = async (
  stationId: string,
  pricing: Pricing
): Promise<void> => {
  const res = await axios.put(`/stations/${stationId}/pricing`, pricing);
  console.log(res.status);
};

export const updateStationTimeZone = async (
  stationId: string,
  timeZone: string
): Promise<void> => {
  const res = await axios.put(`/stations/${stationId}/time-zone`, timeZone, {
    headers: { "Content-Type": "application/json" }
  });
  console.log(res.status);
};

export const updateExclusiveRentalPublicSettings = async (
  stationId: string,
  settings: ExclusiveRentalPublicSettings
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/exclusive-rental-public-display`,
    settings,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceTypeDescription = async (
  stationId: string,
  deviceTypeDescription: DeviceTypeDescription
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/device-type-descriptions`,
    deviceTypeDescription,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceTypeDescriptions = async (
  stationId: string,
  deviceTypeDescriptions: DeviceTypeDescription[]
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/device-type-descriptions`,
    deviceTypeDescriptions,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateSupportContacts = async (
  stationId: string,
  supportContacts: StationSupportContacts
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/support-contacts`,
    supportContacts
  );
  console.log(res.status);
};

export const updateDevicePropertyDescription = async (
  stationId: string,
  devicePropertyDescription: DevicePropertyDescription
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/device-property-descriptions`,
    devicePropertyDescription
  );
  console.log(res.status);
};

export const updateDevicePropertyDescriptions = async (
  stationId: string,
  devicePropertyDescriptions: DevicePropertyDescription[]
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/device-property-descriptions`,
    devicePropertyDescriptions
  );
  console.log(res.status);
};

export const updateDeviceHardwareSettings = async (
  stationId: string,
  deviceNumber: number,
  deviceHardwareSettings: DeviceHardwareSettings
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/hardware-settings`,
    deviceHardwareSettings
  );
  console.log(res.status);
};

export const updateDeviceCapacity = async (
  stationId: string,
  deviceNumber: number,
  capacity: number
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/capacity`,
    capacity,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceMaxRentalDuration = async (
  stationId: string,
  deviceNumber: number,
  maxRentalDuration: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/max-rental-duration`,
    maxRentalDuration,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceMaxPrebookDuration = async (
  stationId: string,
  deviceNumber: number,
  maxPreebookDuration: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/max-prebook-duration`,
    maxPreebookDuration,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateMinInterBookingDuration = async (
  stationId: string,
  deviceNumber: number,
  minInterBookingDuration: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/min-inter-booking-duration`,
    minInterBookingDuration,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceEarlyGracePeriod = async (
  stationId: string,
  deviceNumber: number,
  earlyGracePeriod: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/early-grace-period`,
    earlyGracePeriod,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceLateGracePeriod = async (
  stationId: string,
  deviceNumber: number,
  lateGracePeriod: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/late-grace-period`,
    lateGracePeriod,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceRentalmethod = async (
  stationId: string,
  deviceNumber: number,
  rentalMethod: RentalMethod
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/rental-method`,
    rentalMethod,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceType = async (
  stationId: string,
  deviceNumber: number,
  deviceType: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/type`,
    deviceType,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceDescription = async (
  stationId: string,
  deviceNumber: number,
  description: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/description`,
    description,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

export const updateDeviceDimensions = async (
  stationId: string,
  deviceNumber: number,
  dimensions: DeviceDimensions
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/dimensions`,
    dimensions
  );
  console.log(res.status);
};

export const updateDeviceTypeSettings = async (
  stationId: string,
  deviceNumber: number,
  deviceTypeSettings: DeviceTypeSettings
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/type-settings`,
    deviceTypeSettings
  );
  console.log(res.status);
};

export const enqueueStationFirmwareUpdate = async (
  stationId: string,
  update: FirmwareUpdate
): Promise<void> => {
  const res = await axios.put(`/stations/${stationId}/firmware-update`, update);
  console.log(res.status);
};

export const openDevice = async (
  stationId: string,
  deviceNumber: number
): Promise<boolean> => {
  const res = await axios.post(
    `/stations/${stationId}/devices/${deviceNumber}/open`
  );
  return res.status === 204;
};

export const setDeviceActive = async (
  stationId: string,
  deviceNumber: number,
  active: boolean
): Promise<boolean> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/is-active`,
    active,
    { headers: { "Content-Type": "application/json" } }
  );
  return res.status === 204;
};

export const deleteDevice = async (
  stationId: string,
  deviceNumber: number
): Promise<void> => {
  const res = await axios.delete(
    `/stations/${stationId}/devices/${deviceNumber}`
  );
  console.log(res.status);
};

export const createDevice = async (
  stationId: string,
  device: Device
): Promise<void> => {
  const res = await axios.post(`/stations/${stationId}/devices`, device);
  console.log(res.status);
};

export const updateDeviceTimeZone = async (
  stationId: string,
  deviceNumber: number,
  timeZone: string
): Promise<void> => {
  const res = await axios.put(
    `/stations/${stationId}/devices/${deviceNumber}/time-zone`,
    timeZone,
    { headers: { "Content-Type": "application/json" } }
  );
  console.log(res.status);
};

function toApiDateTime (value: Date | string): string {
  return value instanceof Date ? value.toISOString() : value;
}

function toApiInstantDateTime (value: Date | string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}
